import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RRule, Options as RRuleOptions, datetime } from 'rrule'
import {
  createRecurrenceOptions,
  getRecurrenceDropDownMenuItems,
} from '../../utils/recurrenceUtils'
import { formatDisplayDate } from '../DateFormatter'

/**
 * 다음 3곳에서 DueDate 와 Recurrence 을 통합 처리한다.
 *
 * TodoEditor - DueDateSelector
 * QuickAddTaskEditor (저장 안함)
 * TodoListItem
 */
const COMPONENT_TYPES = {
  DUE_DATE_SELECTOR: 'DueDateSelector',
  QUICK_ADD_TASK_EDITOR: 'QuickAddTaskEditor',
  TODO_LIST_ITEM: 'TodoListItem',
} as const

type ComponentType = (typeof COMPONENT_TYPES)[keyof typeof COMPONENT_TYPES]

/**
 * Date option keys
 *  - today
 *  - tomorrow
 *  - no_date
 *  - pick_date
 *  - recurrence
 */
const DATE_OPTIONS = {
  TODAY: 'today',
  TOMORROW: 'tomorrow',
  NO_DATE: 'no_date',
  PICK_DATE: 'pick_date',
  RECURRENCE: 'recurrence',
} as const

type DateOption = (typeof DATE_OPTIONS)[keyof typeof DATE_OPTIONS]

/**
 * Recurrence rule interface
 */
interface RecurrenceRule {
  recurrenceRule?: RRule
  options?: Partial<RRuleOptions>
  origOptions?: Partial<RRuleOptions>
}

/**
 * 반복 규칙의 첫 발생일을 로컬 Date 로 돌려준다 (startDate 자신이 발생일이면 그대로).
 *
 * rrule 은 tzid 가 없을 때 dtstart 의 UTC 컴포넌트를 벽시계로 해석한다. 따라서 로컬 Date 를
 * 그대로 넘기면 앵커가 오프셋만큼 밀린다(KST 자정 -> rrule 은 전날 15:00 으로 본다). 로컬
 * 벽시계를 datetime() 으로 옮겨 프레임을 맞추고, 결과는 UTC 게터로 읽어 로컬로 되돌린다.
 *
 * getTimezoneOffset() 으로 시각을 직접 가감하거나 +-(offset * 60 * 1000) 을 쓰면 안 된다.
 * DST 에서 값이 변하고, 분 단위 오프셋 지역(India +5:30)에서는 /60 이 정수가 아니다.
 */
export const firstOccurrenceOf = (options: Partial<RRuleOptions>, startDate: Date): Date | null => {
  const anchor = datetime(
    startDate.getFullYear(),
    startDate.getMonth() + 1,
    startDate.getDate(),
    startDate.getHours(),
    startDate.getMinutes(),
    startDate.getSeconds(),
  )
  const occurrence = new RRule({ ...options, dtstart: anchor }).after(anchor, true)
  if (!occurrence) return null

  return new Date(
    occurrence.getUTCFullYear(),
    occurrence.getUTCMonth(),
    occurrence.getUTCDate(),
    occurrence.getUTCHours(),
    occurrence.getUTCMinutes(),
    occurrence.getUTCSeconds(),
  )
}

/**
 * Edit data interface for DueDateSelector
 */
interface EditData {
  dueDate?: Date | null
  recurrenceRule?: RecurrenceRule | null
}

/**
 * RRule data interface for TodoListItem
 */
interface RRuleData {
  recurrenceRule?: RRule | null
}

/**
 * Update handlers for TodoListItem
 */
interface UpdateHandlers {
  updateDueDate?: (date: Date | null, callback?: (success: boolean) => void) => void
  updateRecurrence?: (
    rrule: RRule | null,
    startDate: Date | null,
    callback?: (success: boolean) => void,
  ) => void
}

/**
 * Menu item interface for Ant Design Dropdown
 */
interface MenuItem {
  key: string
  label?: React.ReactNode
  onClick?: (e: { domEvent: React.MouseEvent }) => void
  type?: 'divider'
  children?: MenuItem[]
  popupClassName?: string
}

/**
 * Hook configuration interface
 */
interface UseDueDateRecurrenceConfig {
  dueDate: Date | null
  onDueDateChange?: ((date: Date | null, rrule: RRule | null) => void) | null
  rrule?: RRuleData | null // todoList 에서만 사용.
  onRecurrenceChange?: ((rrule: RRule | null) => void) | null
  editData?: EditData | null // editor 에서만 사용.
  onTodoListUpdate?: (() => void) | null // todoList 에서만 사용.
  updateHandlers?: UpdateHandlers | null // todoList 에서만 사용.
  onOpenDatePicker?:
    | ((type: string, date: Date | null, time: null, callback: (date: Date | null) => void) => void)
    | null // Date picker를 열기 위한 콜백
  onOpenRecurrenceEditor?:
    | ((
        rrule: RRule | null,
        startDate: Date,
        options: Partial<RRuleOptions> | null,
        callback: (options: Partial<RRuleOptions> | null, startDate: Date) => void,
      ) => void)
    | null // Recurrence editor를 열기 위한 콜백
}

/**
 * Hook return type
 */
interface UseDueDateRecurrenceReturn {
  // State
  dueDateDropdownOpen: boolean
  setDueDateDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  datePickerOpen?: boolean
  setDatePickerOpen?: () => void
  showRecurrenceEditor?: boolean
  setShowRecurrenceEditor?: React.Dispatch<React.SetStateAction<boolean>>

  // Handlers
  handleDateChange: (date: Date | null) => void
  handleDatePickerClose: () => void
  handleQuickDateSelect: (option: DateOption) => void
  handleRecurrenceMenuClick: (key: string) => void
  handleCustomRecurrenceSave: (options: Partial<RRuleOptions> | null, startDate: Date) => void

  // Utils
  createDueDateDropdownMenuItems: (styles: any, includeNoDate?: boolean) => MenuItem[]
  getDueDateInfoText: () => string

  // Additional helpers
  componentType: ComponentType
  currentRecurrenceRule: RRule | null
}

/**
 * Common hook for handling due date and recurrence functionality
 * across DueDateSelector, QuickAddTaskEditor, and TodoListItem
 *
 * @param {Object} config - Hook configuration
 * @param {Date|null} config.value - Current due date
 * @param {Function} config.onChange - Date change handler
 * @param {Function} config.onRecurrenceChange - Recurrence change handler
 * @param {Object} config.editData - Data for editing (contains recurrenceRule)
 * @param {Function} config.onTodoListUpdate - Update handler for TodoListItem
 * @param {Object} config.rrule - RRule object for TodoListItem
 * @param {Object} config.updateHandlers - Custom update handlers { updateDueDate, updateRecurrence }
 *
 *   1. 사용자가 TodoListItem에서 dueDate/reminder 변경
 *   2. useDueDateRecurrence → updateHandlers.updateDueDate/updateRecurrence
 *   3. TodoUpdater.updateTodoDueDate/updateTodoReminder/updateTodoRecurrence
 *   4. TodoManager.updateTask:
 *    - ✅ Optimistic update (즉시 메모리 캐시 업데이트)
 *    - ✅ 서버로 CalDAV 업데이트 요청
 *    - ✅ 성공 시: 서버 응답으로 최종 데이터 업데이트
 *    - ✅ 실패 시: rollback으로 원본 데이터 복구
 *   5. saveTodo에서 todoItemEmitter.emit('item') 트리거
 *   6. UI 컴포넌트들이 새로고침되어 변경사항 반영
 */
const useDueDateRecurrence = ({
  dueDate,
  onDueDateChange = null,
  rrule = null, // todoList 에서만 사용.
  onRecurrenceChange = null,
  editData = null, // editor 에서만 사용.
  onTodoListUpdate = null, // todoList 에서만 사용.
  updateHandlers = null, // todoList 에서만 사용.
  onOpenDatePicker = null, // Date picker를 열기 위한 콜백
  onOpenRecurrenceEditor = null, // Recurrence editor를 열기 위한 콜백
}: UseDueDateRecurrenceConfig): UseDueDateRecurrenceReturn => {
  const { t } = useTranslation()

  // State management (only keep dropdown state, date picker and recurrence editor are handled by parent)
  const [dueDateDropdownOpen, setDueDateDropdownOpen] = useState<boolean>(false)
  // Keep these states for non-TodoListItem components
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false)
  const [showRecurrenceEditor, setShowRecurrenceEditor] = useState<boolean>(false)

  // Determine component type for conditional logic
  const componentType: ComponentType = useMemo(() => {
    if (updateHandlers) return COMPONENT_TYPES.TODO_LIST_ITEM
    if (editData) return COMPONENT_TYPES.DUE_DATE_SELECTOR
    return COMPONENT_TYPES.QUICK_ADD_TASK_EDITOR
  }, [updateHandlers, editData])

  // Get current recurrence rule
  const currentRecurrenceRule: RRule | null = useMemo(() => {
    return editData?.recurrenceRule?.recurrenceRule || rrule?.recurrenceRule || null
  }, [editData?.recurrenceRule, rrule?.recurrenceRule])

  /**
   * DropDown 에서 today 와 tomorrow 날짜를 선택할 때 사용한다.
   */
  const getTodayAndTomorrowDates = useCallback(() => {
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)

    // Set time to 00:00:00 for date-only selections
    today.setHours(0, 0, 0, 0)
    tomorrow.setHours(0, 0, 0, 0)

    return { today, tomorrow }
  }, [])

  /**
   * DueDate 날짜 변경 처리
   *  - 날짜 선택 시 기존에 있던 Recurrence 은 제거한다.
   */
  const handleDateChange = useCallback(
    (date: Date | null) => {
      const normalizedDate = date || null

      if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM) {
        // Clear recurrence when manually picking a date
        if (currentRecurrenceRule && updateHandlers?.updateRecurrence) {
          updateHandlers.updateRecurrence(null, normalizedDate, (success) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else if (updateHandlers?.updateDueDate) {
          updateHandlers.updateDueDate(normalizedDate, (success) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        }
      } else {
        // For DueDateSelector and QuickAddTaskEditor
        if (onDueDateChange) {
          onDueDateChange(normalizedDate, null)
        }
      }
    },
    [componentType, currentRecurrenceRule, updateHandlers, onTodoListUpdate, onDueDateChange],
  )

  /**
   * Handle date picker close
   */
  const handleDatePickerClose = useCallback(() => {
    if (componentType !== COMPONENT_TYPES.TODO_LIST_ITEM) {
      setDatePickerOpen(false)
    }
    // For TodoListItem, this is handled by the parent component
  }, [componentType])

  /**
   * Handle custom recurrence save with timezone handling for DueDateSelector
   */
  const handleCustomRecurrenceSave = useCallback(
    (options: Partial<RRuleOptions> | null, startDate: Date) => {
      if (options) {
        try {
          const rrule = new RRule(options)

          if (
            componentType === COMPONENT_TYPES.TODO_LIST_ITEM &&
            updateHandlers?.updateRecurrence
          ) {
            updateHandlers.updateRecurrence(rrule, startDate, (success: boolean) => {
              if (success && onTodoListUpdate) onTodoListUpdate()
            })
          } else {
            // DueDateSelector 는 사용자가 고른 날짜가 반복 규칙에 맞지 않을 때
            // 첫 발생일로 당겨서 마감일에 반영한다. rrule 조회가 필요한 유일한 분기다.
            if (componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR) {
              try {
                if (onDueDateChange) {
                  onDueDateChange(firstOccurrenceOf(options, startDate) ?? startDate, rrule)
                }
              } catch (recurrenceError) {
                console.warn('Failed to resolve first occurrence:', recurrenceError)
                if (onDueDateChange) onDueDateChange(startDate, null)
              }
            } else {
              // For QuickAddTaskEditor
              if (onDueDateChange) onDueDateChange(startDate, null)
            }

            if (onRecurrenceChange) onRecurrenceChange(rrule)
          }
        } catch (error) {
          console.error('Error creating custom recurrence:', error)
        }
      } else {
        // Handle recurrence removal
        if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && updateHandlers?.updateRecurrence) {
          updateHandlers.updateRecurrence(null, null, (success: boolean) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else {
          if (onRecurrenceChange) onRecurrenceChange(null)
        }
      }
      // Only close recurrence editor if not using callbacks (for backwards compatibility)
      if (componentType !== COMPONENT_TYPES.TODO_LIST_ITEM) {
        setShowRecurrenceEditor(false)
      }
    },
    [componentType, updateHandlers, onTodoListUpdate, onDueDateChange, onRecurrenceChange],
  )

  /**
   * DropDown 에서 날짜를 선택할 때 사용
   *  - today
   *  - tomorrow
   *  - no_date
   *  - pick_date
   *  - recurrence
   */
  const handleQuickDateSelect = useCallback(
    (option: DateOption) => {
      const { today, tomorrow } = getTodayAndTomorrowDates()
      let selectedDate: Date | null = null

      switch (option) {
        case DATE_OPTIONS.TODAY:
          selectedDate = new Date(today)
          break
        case DATE_OPTIONS.TOMORROW:
          selectedDate = new Date(tomorrow)
          break
        case DATE_OPTIONS.NO_DATE:
          selectedDate = null
          break
        case DATE_OPTIONS.PICK_DATE:
          setDueDateDropdownOpen(false)
          if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && onOpenDatePicker) {
            onOpenDatePicker('DATE', dueDate, null, handleDateChange)
          } else {
            setDatePickerOpen(true)
          }
          return
        case DATE_OPTIONS.RECURRENCE:
          setDueDateDropdownOpen(false)
          if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && onOpenRecurrenceEditor) {
            onOpenRecurrenceEditor(
              null,
              dueDate || new Date(),
              currentRecurrenceRule?.options || null,
              handleCustomRecurrenceSave,
            )
          } else {
            setShowRecurrenceEditor(true)
          }
          return
        default:
          console.warn(`Unknown date option: ${option}`)
          return
      }

      if (selectedDate) {
        selectedDate.setHours(0, 0, 0, 0)
      }

      // Handle different update patterns based on component type
      if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM) {
        if (currentRecurrenceRule && updateHandlers?.updateRecurrence) {
          // Clear recurrence when selecting other date options
          updateHandlers.updateRecurrence(null, selectedDate, (success) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else if (updateHandlers?.updateDueDate) {
          updateHandlers.updateDueDate(selectedDate, (success) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else {
          if (onDueDateChange) onDueDateChange(selectedDate, null)
        }
      } else {
        // For DueDateSelector and QuickAddTaskEditor

        if (onDueDateChange) onDueDateChange(selectedDate, null)
        // Clear recurrence when selecting other date options
        // if (onRecurrenceChange) onRecurrenceChange(null)
      }

      setDueDateDropdownOpen(false)
    },
    [
      getTodayAndTomorrowDates,
      componentType,
      currentRecurrenceRule,
      updateHandlers,
      onTodoListUpdate,
      onDueDateChange,
      dueDate,
      onOpenDatePicker,
      handleDateChange,
      onOpenRecurrenceEditor,
      handleCustomRecurrenceSave,
    ],
  )

  /**
   * Recurrence dropdown menu 선택 처리
   *  - never
   *  - daily
   *  - weekly
   *  - biweekly
   *  - monthly
   *  - 3monthly
   *  - 6monthly
   *  - yearly
   *  - custom
   */
  const handleRecurrenceMenuClick = useCallback(
    (key: string) => {
      if (key === 'never') {
        if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && updateHandlers?.updateRecurrence) {
          updateHandlers.updateRecurrence(null, null, (success) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else {
          if (onRecurrenceChange) onRecurrenceChange(null)
        }
        setDueDateDropdownOpen(false)
        return
      }

      if (key === 'custom') {
        setDueDateDropdownOpen(false)
        if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && onOpenRecurrenceEditor) {
          onOpenRecurrenceEditor(
            null,
            dueDate || new Date(),
            currentRecurrenceRule?.options || null,
            handleCustomRecurrenceSave,
          )
        } else {
          setShowRecurrenceEditor(true)
        }
        return
      }

      const rruleOptions = createRecurrenceOptions(key, dueDate)
      if (!rruleOptions) {
        console.warn(`Failed to create recurrence options for key: ${key}`)
        setDueDateDropdownOpen(false)
        return
      }

      try {
        const rrule = new RRule(rruleOptions as Partial<RRuleOptions>)
        const startDate = dueDate ? new Date(dueDate) : new Date()

        if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM && updateHandlers?.updateRecurrence) {
          updateHandlers.updateRecurrence(rrule, startDate, (success: boolean) => {
            if (success && onTodoListUpdate) onTodoListUpdate()
          })
        } else {
          if (onRecurrenceChange) onRecurrenceChange(rrule)
          // For DueDateSelector and QuickAddTaskEditor, also update the date
          if (onDueDateChange) {
            // For DueDateSelector, pass both startDate and rrule
            if (componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR) {
              onDueDateChange(startDate, rrule)
            } else {
              // For QuickAddTaskEditor, just pass the date
              onDueDateChange(startDate, null)
            }
          }
        }
      } catch (error) {
        console.error('Error creating RRule:', error)
      }

      setDueDateDropdownOpen(false)
    },
    [
      componentType,
      updateHandlers,
      onTodoListUpdate,
      onRecurrenceChange,
      dueDate,
      onOpenRecurrenceEditor,
      currentRecurrenceRule?.options,
      handleCustomRecurrenceSave,
      onDueDateChange,
    ],
  )

  /**
   * DueDate dropdown menu items
   *  - today
   *  - tomorrow
   *  - no_date
   *  - pick_date
   *  - recurrence
   */

  // Create menu items based on component type
  const createMenuItems = useCallback(
    (styles: any, _includeNoDate?: boolean): MenuItem[] => {
      const baseItems: MenuItem[] = [
        {
          key: DATE_OPTIONS.TODAY,
          label: (
            <div className={styles.menuItem}>
              <span>{t('todo.editor.today')}</span>
            </div>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation()
            handleQuickDateSelect(DATE_OPTIONS.TODAY)
            setDueDateDropdownOpen(false)
          },
        },
        {
          key: DATE_OPTIONS.TOMORROW,
          label: (
            <div className={styles.menuItem}>
              <span>{t('todo.editor.tomorrow')}</span>
            </div>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation()
            handleQuickDateSelect(DATE_OPTIONS.TOMORROW)
            setDueDateDropdownOpen(false)
          },
        },
      ]

      baseItems.push(
        {
          key: 'separator_pick_date',
          type: 'divider' as const,
        },
        {
          key: DATE_OPTIONS.PICK_DATE,
          label: (
            <div className={styles.menuItem}>
              <span>{t('todo.editor.pickDate')}</span>
            </div>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation()
            handleQuickDateSelect(DATE_OPTIONS.PICK_DATE)
            setDueDateDropdownOpen(false)
          },
        },
      )

      // Add recurrence submenu only for DUE_DATE_SELECTOR
      if (componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR) {
        baseItems.push(
          {
            key: 'separator_recurrence',
            type: 'divider' as const,
          },
          {
            key: 'recurrence_submenu',
            label: (
              <div>
                <span>{t('todo.editor.editRecurrence')}</span>
              </div>
            ),
            children: getRecurrenceDropDownMenuItems(
              t,
              styles,
              handleRecurrenceMenuClick,
            ) as unknown as MenuItem[],
            popupClassName: 'recurrence-dropdown-submenu',
          },
        )

        // Add conditional "No Date" option when a date is set
        const hasDate = editData?.dueDate
        if (hasDate) {
          baseItems.push(
            {
              key: 'separator_no_date',
              type: 'divider' as const,
            },
            {
              key: DATE_OPTIONS.NO_DATE,
              label: (
                <div className={styles.menuItem}>
                  <span>{t('todo.todolist.noDate')}</span>
                </div>
              ),
              onClick: (e) => {
                e.domEvent.stopPropagation()
                handleQuickDateSelect(DATE_OPTIONS.NO_DATE)
              },
            },
          )
        }
      } else {
        if (dueDate || componentType === COMPONENT_TYPES.TODO_LIST_ITEM) {
          // Add No Date option for non-selector components
          baseItems.push({
            key: DATE_OPTIONS.NO_DATE,
            label: (
              <div className={styles.menuItem}>
                <span>{t('todo.todolist.noDate')}</span>
              </div>
            ),
            onClick: (e) => {
              e.domEvent.stopPropagation()
              handleQuickDateSelect(DATE_OPTIONS.NO_DATE)
            },
          })
        }
      }

      return baseItems
    },
    [
      componentType,
      t,
      editData?.dueDate,
      dueDate,
      handleQuickDateSelect,
      handleRecurrenceMenuClick,
    ],
  )
  const createDueDateDropdownMenuItems = createMenuItems

  /**
   * DueDate 표시 텍스트 생성 (TodoEditor)
   */
  const [dueDateDisplayText, setDueDateDisplayText] = useState<string>('')

  useEffect(() => {
    // console.log('🔄 dueDateDisplayText: Recalculating display text')
    // console.log('  - dueDate:', dueDate)
    // console.log('  - editData?.dueDate:', editData?.dueDate)
    // console.log('  - componentType:', componentType)
    // console.log('  - currentRecurrenceRule:', currentRecurrenceRule)

    let newDisplayText: string | null

    // recurrenceRule 이 있으면 recurrenceRule 표시 -> editor 에서만 표시한다.
    if (componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR && currentRecurrenceRule) {
      newDisplayText = t('todo.editor.editRecurrence')
      // getCurrentRecurrenceDescription(currentRecurrenceRule, t)
      console.log('  - calculated recurrence description:', newDisplayText)
    } else {
      // DueDateSelector의 경우 editData.dueDate를 우선시함
      const effectiveValue =
        componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR
          ? editData?.dueDate !== undefined
            ? editData.dueDate
            : dueDate
          : dueDate

      // console.log('  - editData?.dueDate !== undefined:', editData?.dueDate !== undefined)
      // console.log(
      //   '  - Using editData.dueDate:',
      //   editData?.dueDate !== undefined ? editData.dueDate : 'fallback to dueDate',
      // )
      // console.log('  - effectiveValue:', effectiveValue)

      if (!effectiveValue) {
        if (componentType === COMPONENT_TYPES.DUE_DATE_SELECTOR) {
          newDisplayText = t('todo.todolist.noDate')
        } else if (componentType === COMPONENT_TYPES.QUICK_ADD_TASK_EDITOR) {
          // QuickAddTaskEditor에서도 "No Date" 표시
          newDisplayText = null // 빈 상태로 표시 (아이콘만 표시)
        } else {
          // TodoListItem 에서는 표시하지 않는다.
          newDisplayText = null
        }
      } else {
        try {
          // QuickAddTaskEditor와 DueDateSelector에서는 날짜 표시
          if (componentType === COMPONENT_TYPES.TODO_LIST_ITEM) {
            // TodoListItem에서는 표시하지 않음
            newDisplayText = null
          } else {
            // DueDateSelector와 QuickAddTaskEditor에서는 날짜 표시
            newDisplayText = formatDisplayDate
              ? formatDisplayDate(effectiveValue, t)
              : effectiveValue.toLocaleDateString()
            console.log('  - calculated formatted date:', newDisplayText)
          }
        } catch (error) {
          console.error('Error formatting display date:', error)
          newDisplayText = effectiveValue?.toString() || null
          console.log('  - calculated fallback date:', newDisplayText)
        }
      }
    }

    // Update state with calculated result
    if (newDisplayText !== dueDateDisplayText) {
      console.log('📝 Updating dueDateDisplayText from:', dueDateDisplayText, 'to:', newDisplayText)
      setDueDateDisplayText(newDisplayText || '')
    }
  }, [
    dueDate,
    editData?.dueDate,
    editData,
    t,
    currentRecurrenceRule,
    componentType,
    formatDisplayDate,
    dueDateDisplayText,
  ])

  return {
    // State (only keep what's needed for backwards compatibility)
    dueDateDropdownOpen,
    setDueDateDropdownOpen,
    // For non-TodoListItem components that still need these
    ...(componentType !== COMPONENT_TYPES.TODO_LIST_ITEM && {
      datePickerOpen,
      setDatePickerOpen: () => setDatePickerOpen(false),
      showRecurrenceEditor,
      setShowRecurrenceEditor,
    }),

    // Handlers
    handleDateChange, // dueDate 변경 처리
    handleDatePickerClose, // datePicker 닫기
    handleQuickDateSelect, // 날짜 선택
    handleRecurrenceMenuClick, // recurrence 선택
    handleCustomRecurrenceSave, // custom recurrence 저장

    // Utils
    createDueDateDropdownMenuItems, // dueDate dropdown menu items 생성
    getDueDateInfoText: () => dueDateDisplayText, // dueDate 표시 텍스트 생성

    // Additional helpers
    componentType,
    currentRecurrenceRule,
  }
}

export default useDueDateRecurrence
export { DATE_OPTIONS }
export type {
  UseDueDateRecurrenceConfig,
  UseDueDateRecurrenceReturn,
  DateOption,
  ComponentType,
  RecurrenceRule,
  EditData,
  RRuleData,
  UpdateHandlers,
  MenuItem,
}
